import { createSlice } from "@reduxjs/toolkit";
import { setProp } from "Store/Actions";
import { setPropT } from "Store/Actions/ts";
import setObj from "lodash/set";
import { submitRegistration, fetchUserOnboardingList, addEditUser, submitAction } from "../Services/OnboardingThunk";

interface UserOnboardingData {
    id: string;
    userName: string;
    email: string;
    company: string;
    initiatedOn: string;
    status: string;
}

interface RegistrationFormData {
    firstName: string;
    lastName: string;
    contactRole: string;
    email: string;
    phoneCode: string | null;
    phoneNumber: string;
    sixStarNumber: string;
    accountOnboardingStatus: string;
    accountNumber: string;
    companyName: string;
    companyWebsite: string;
    companyIndustry: string;
    companyVATNumber: string;
    billingCountry: string;
    billingStreet: string;
    billingCity: string;
    billingState: string;
    billingZipCode: string;
    shippingCountry: string;
    shippingStreet: string;
    shippingCity: string;
    shippingState: string;
    shippingZipCode: string;
}

interface UserOnboardingDetails {
    personalDetails: {
        firstName: string;
        lastName: string;
        contactRole: string;
        email: string;
        phoneCode: string;
        phoneNumber: string;
    };
    companyDetails: {
        companyName: string;
        companyWebsite: string;
        companyIndustry: string;
        companyVATNumber: string;
        billingAddress: {
            country: string;
            street: string;
            city: string;
            state: string;
            zipCode: string;
        };
        shippingAddress: {
            country: string;
            street: string;
            city: string;
            state: string;
            zipCode: string;
        };
    };
}

interface OnboardingState {
    loading: boolean;
    error: string | null;
    userOnboardingData: UserOnboardingData[];
    searchText: string;
    summary: {
        total: number;
        active: number;
        pending: number;
        inactive: number;
    };
    showDetailsPanel: boolean;
    selectedUser: any | null; 
    userDetails: UserOnboardingDetails | null;
    detailsLoading: boolean;
    detailsError: string | null;
    registrationForm: RegistrationFormData;
    registrationValidations: Partial<Record<keyof RegistrationFormData, string>>;
    currentStep: number;
    registrationLoading: boolean;
    registrationError: string | null;
    registrationSuccess: boolean;
    clearSelection: boolean;
    actionLoading: boolean;
    actionError: string | null;
}

const initialState: OnboardingState = {
    loading: false,
    error: null,
    userOnboardingData: [],
    searchText: "",
    summary: {
        total: 0,
        active: 0,
        pending: 0,
        inactive: 0,
    },
    showDetailsPanel: false,
    selectedUser: null,
    userDetails: null,
    detailsLoading: false,
    detailsError: null,
    registrationForm: {
        firstName: '',
        lastName: '',
        contactRole: '',
        email: '',
        phoneCode: null,
        phoneNumber: '',
        sixStarNumber: '',
        accountOnboardingStatus: '',
        accountNumber: '',
        companyName: '',
        companyWebsite: '',
        companyIndustry: '',
        companyVATNumber: '',
        billingCountry: '',
        billingStreet: '',
        billingCity: '',
        billingState: '',
        billingZipCode: '',
        shippingCountry: '',
        shippingStreet: '',
        shippingCity: '',
        shippingState: '',
        shippingZipCode: '',
    },
    registrationValidations: {},
    currentStep: 0,
    registrationLoading: false,
    registrationError: null,
    registrationSuccess: false,
    clearSelection: false,
    actionLoading: false,
    actionError: null,
};

const onboardingSlice = createSlice({
    name: "onboarding",
    initialState: initialState,
    reducers: {
        setSelectedUser: (state, action) => {
            state.selectedUser = action.payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchUserOnboardingList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserOnboardingList.fulfilled, (state, action) => {
                state.loading = false;
                state.userOnboardingData = action.payload.data;
            })
            .addCase(fetchUserOnboardingList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch user onboarding list";
            })
            .addCase(setProp, (state, action: setPropT) => {
                if (action.payload.slice === onboardingSlice.name) {
                    setObj(state, action.payload.key, action.payload.value);
                }
            })
            .addCase(submitRegistration.pending, (state) => {
                state.registrationLoading = true;
                state.registrationError = null;
            })
            .addCase(addEditUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addEditUser.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(addEditUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to save user";
            })
            .addCase(submitRegistration.fulfilled, (state, action) => {
                state.registrationLoading = false;
                if (action.payload.status) {
                    state.registrationSuccess = true;
                    state.registrationForm = initialState.registrationForm;
                    state.registrationValidations = {};
                    state.currentStep = 0;
                }
            })
            .addCase(submitRegistration.rejected, (state, action) => {
                state.registrationLoading = false;
                state.registrationError = action.error.message || "Registration failed";
            })
            .addCase(submitAction.pending, (state) => {
                state.actionLoading = true;
                state.actionError = null;
            })
            .addCase(submitAction.fulfilled, (state, action) => {
                state.actionLoading = false;
                if (action.payload.status) {
                    state.showDetailsPanel = false;
                    state.selectedUser = null;
                    state.clearSelection = !state.clearSelection;
                }
            })
            .addCase(submitAction.rejected, (state, action) => {
                state.actionLoading = false;
                state.actionError = action.error.message || "Action failed";
            });
    },
});

export const { setSelectedUser} = onboardingSlice.actions;
export default onboardingSlice.reducer;